/**
 * FeatureGate Component
 * 
 * Conditionally renders content based on user's access to features.
 * Supports multiple display modes: hide, blur, or show upgrade prompt.
 */

import React from 'react';
import { hasAccess } from '../utils/accessControl';
import './FeatureGate.css';

const FeatureGate = ({ 
  user, 
  featureKey, 
  children, 
  fallback = null,
  blur = false,
  showUpgradePrompt = false,
  upgradePlanName = 'Pro',
  customMessage = null
}) => {
  console.log('🚪 [FeatureGate] Rendering:', {
    featureKey,
    hasUser: !!user,
    blur,
    showUpgradePrompt,
    userRole: user?.role,
    isDeveloper: user?.isDeveloper
  });

  const hasFeatureAccess = hasAccess(user, featureKey);

  console.log(`🚪 [FeatureGate] Access check result: ${hasFeatureAccess ? '✅ GRANTED' : '❌ DENIED'} for feature: ${featureKey}`);

  // User has access - render children normally
  if (hasFeatureAccess) {
    console.log('🚪 [FeatureGate] Rendering children (access granted)');
    return <>{children}</>;
  }

  // User doesn't have access
  
  // Mode 1: Show custom fallback
  if (fallback && !blur && !showUpgradePrompt) {
    return <>{fallback}</>;
  }

  // Mode 2: Blur content with optional overlay
  if (blur || showUpgradePrompt) {
    console.log('🚪 [FeatureGate] Rendering with blur/overlay (access denied)');
    return (
      <div className="feature-gate-container">
        <div className={`feature-gate-content ${blur ? 'feature-gate-blur' : ''}`}>
          {children}
        </div>
        {showUpgradePrompt && (
          <UpgradePrompt 
            featureKey={featureKey}
            planName={upgradePlanName}
            customMessage={customMessage}
          />
        )}
      </div>
    );
  }

  // Mode 3: Hide completely (default)
  console.log('🚪 [FeatureGate] Hiding content completely (access denied, no blur)');
  return null;
};

/**
 * Upgrade Prompt Overlay
 * Shown when feature is not available
 */
const UpgradePrompt = ({ featureKey, planName, customMessage }) => {
  const handleUpgradeClick = () => {
    // Navigate to upgrade page or show contact modal
    // For now, this is a placeholder - billing is handled out-of-band
    alert('Please contact your account manager to upgrade your plan.');
  };

  return (
    <div className="feature-gate-overlay">
      <div className="feature-gate-prompt">
        <div className="feature-gate-icon">🔒</div>
        <h3 className="feature-gate-title">Feature Locked</h3>
        <p className="feature-gate-message">
          {customMessage || `This feature requires the ${planName} plan or higher.`}
        </p>
        <p className="feature-gate-submessage">
          Contact your administrator to unlock this feature.
        </p>
        <button 
          className="feature-gate-button"
          onClick={handleUpgradeClick}
        >
          Contact Sales
        </button>
      </div>
    </div>
  );
};

/**
 * FeatureButton - Button that's disabled if feature not available
 */
export const FeatureButton = ({ 
  user, 
  featureKey, 
  onClick, 
  children,
  className = '',
  disabled = false,
  showTooltip = true,
  ...props 
}) => {
  const hasFeatureAccess = hasAccess(user, featureKey);
  const isDisabled = disabled || !hasFeatureAccess;

  const handleClick = (e) => {
    if (!hasFeatureAccess) {
      e.preventDefault();
      if (showTooltip) {
        alert('This feature is not available on your current plan.');
      }
      return;
    }
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <button
      className={`${className} ${!hasFeatureAccess ? 'feature-disabled' : ''}`}
      onClick={handleClick}
      disabled={isDisabled}
      title={!hasFeatureAccess ? 'Feature not available on your plan' : ''}
      {...props}
    >
      {children}
      {!hasFeatureAccess && showTooltip && ' 🔒'}
    </button>
  );
};

/**
 * FeatureLink - Link that's disabled if feature not available
 */
export const FeatureLink = ({ 
  user, 
  featureKey, 
  to, 
  children,
  className = '',
  showTooltip = true,
  ...props 
}) => {
  const hasFeatureAccess = hasAccess(user, featureKey);

  const handleClick = (e) => {
    if (!hasFeatureAccess) {
      e.preventDefault();
      if (showTooltip) {
        alert('This feature is not available on your current plan.');
      }
    }
  };

  return (
    <a
      href={hasFeatureAccess ? to : '#'}
      className={`${className} ${!hasFeatureAccess ? 'feature-disabled' : ''}`}
      onClick={handleClick}
      title={!hasFeatureAccess ? 'Feature not available on your plan' : ''}
      {...props}
    >
      {children}
      {!hasFeatureAccess && showTooltip && ' 🔒'}
    </a>
  );
};

/**
 * FeatureBadge - Shows feature availability status
 */
export const FeatureBadge = ({ user, featureKey, showAvailable = false }) => {
  const hasFeatureAccess = hasAccess(user, featureKey);

  if (hasFeatureAccess && !showAvailable) {
    return null; // Don't show badge if user has access (unless explicitly requested)
  }

  return (
    <span className={`feature-badge ${hasFeatureAccess ? 'feature-available' : 'feature-locked'}`}>
      {hasFeatureAccess ? '✓ Available' : '🔒 Locked'}
    </span>
  );
};

export default FeatureGate;
